import { calculateAgeFromDob } from "./patientFormHelpers";

export const parseImagePaths = (paths) => (paths ? paths.split(",").filter(Boolean) : []);

const ESTB_SKIP_KEYS = new Set([
  "establishment_type",
  "establishment_sub_type",
  "parent_establishment_name",
  "parent_establishment_display",
  "establishment_type_display",
  "establishment_sub_type_display",
  "country_name",
  "state_name",
  "city_name",
  "location_name",
  "created_on",
  "last_updated_on",
  "establishment",
]);

const ESTB_JSON_FIELDS = [
  "awards_list",
  "news_list",
  "knowledge_list",
  "phone_numbers",
  "removed_gallery_ids",
];

/** Build multipart FormData for establishment POST/PUT (matches backend parser). */
export const buildEstablishmentFormData = (data) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (ESTB_SKIP_KEYS.has(k)) return;

    if (k === "gallery_images" && Array.isArray(v)) {
      v.forEach((item) => {
        if (item instanceof File) fd.append("gallery_images", item);
      });
      return;
    }
    if (k === "profile_pdf" && v instanceof File) fd.append("profile_pdf", v);
    else if (k === "logo" && v instanceof File) fd.append("logo", v);
    else if (k === "registration_certificate" && v instanceof File) {
      fd.append("registration_certificate", v);
    } else if (k === "engagement_letter" && v instanceof File) {
      fd.append("engagement_letter", v);
    } else if (ESTB_JSON_FIELDS.includes(k) && Array.isArray(v)) {
      fd.append(k, JSON.stringify(v));
    } else if (
      v !== null &&
      v !== undefined &&
      !(v instanceof File) &&
      !Array.isArray(v) &&
      typeof v !== "object"
    ) {
      fd.append(k, v);
    }
  });
  return fd;
};

const DOCTOR_SKIP_KEYS = new Set([
  "establishment_name",
  "user_type",
  "medical_specialty",
  "department_name",
  "designation",
  "country_name",
  "state_name",
  "city_name",
  "location_name",
  "created_on",
  "last_updated_on",
]);

const DOCTOR_JSON_FIELDS = ["phone_numbers"];

const PATIENT_SKIP_KEYS = new Set([
  "referred_by_doctor_name",
  "blood_group",
  "nationality_name",
  "country_name",
  "location_name",
  "city_name",
  "state_name",
  "emergency_contact_1_relationship",
  "emergency_contact_2_relationship",
  "profile_pic_path",
  "created_on",
  "last_updated_on",
]);

/** Build JSON payload for patient POST/PUT. */
export const buildPatientPayload = (data) => {
  const payload = {};
  Object.entries(data).forEach(([k, v]) => {
    if (PATIENT_SKIP_KEYS.has(k)) return;
    if (k === "phone_numbers" && Array.isArray(v)) {
      payload[k] = v.map((p) => (p || "").trim()).filter(Boolean);
    } else if (v !== null && v !== undefined && !(v instanceof File)) {
      payload[k] = v === "" ? null : v;
    }
  });
  if (payload.patient_id != null) delete payload.patient_id;
  if (payload.date_of_birth && !payload.age_years) {
    payload.age_years = calculateAgeFromDob(payload.date_of_birth);
  }
  if (!payload.date_of_birth) {
    payload.age_years = payload.age_years ?? null;
  }
  return payload;
};

/** Build multipart FormData for patient POST/PUT. */
export const buildPatientFormData = (data) => {
  const payload = buildPatientPayload(data);
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (k === "phone_numbers" && Array.isArray(v)) {
      fd.append(k, JSON.stringify(v));
    } else if (v !== null && v !== undefined) {
      fd.append(k, v);
    }
  });
  if (data.profile_pic instanceof File) {
    fd.append("profile_pic", data.profile_pic);
  }
  return fd;
};

/** Build multipart FormData for doctor POST/PUT. */
export const buildDoctorFormData = (data) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (DOCTOR_SKIP_KEYS.has(k)) return;

    if (k === "profile_pdf" && v instanceof File) fd.append("profile_pdf", v);
    else if (k === "profile_pic" && v instanceof File) fd.append("profile_pic", v);
    else if (k === "registration_certificate" && v instanceof File) {
      fd.append("registration_certificate", v);
    } else if (k === "engagement_letter" && v instanceof File) {
      fd.append("engagement_letter", v);
    } else if (DOCTOR_JSON_FIELDS.includes(k) && Array.isArray(v)) {
      fd.append(k, JSON.stringify(v));
    } else if (
      v !== null &&
      v !== undefined &&
      !(v instanceof File) &&
      !Array.isArray(v) &&
      typeof v !== "object"
    ) {
      fd.append(k, v);
    }
  });
  return fd;
};

const USER_SKIP_KEYS = new Set([
  "user_type",
  "country_name",
  "location_name",
  "establishment_name",
  "created_on",
  "last_updated_on",
]);

/** Build multipart FormData for user master POST/PUT. */
export const buildUserFormData = (data) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (USER_SKIP_KEYS.has(k)) return;
    if (k === "profile_pic" && v instanceof File) fd.append("profile_pic", v);
    else if (
      v !== null &&
      v !== undefined &&
      !(v instanceof File) &&
      !Array.isArray(v) &&
      typeof v !== "object"
    ) {
      fd.append(k, v);
    }
  });
  return fd;
};

export const parseApiError = async (res) => {
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    return data.message || `Server error ${res.status}`;
  } catch {
    return text.trim() || `Server error ${res.status}`;
  }
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

export const formatProcCostRange = (low, high, locale, currencySymbol) => {
  const fmt = (n) => {
    if (n == null || n === "") return null;
    const num = Number(n);
    return Number.isFinite(num)
      ? `${currencySymbol}${num.toLocaleString(locale)}`
      : null;
  };
  const lowLabel = fmt(low);
  const highLabel = fmt(high);
  if (lowLabel && highLabel) return `${lowLabel} – ${highLabel}`;
  return lowLabel || highLabel || "—";
};

export const formatProcCostUsd = (low, high) => formatProcCostRange(low, high, "en-US", "$");
export const formatProcCostInr = (low, high) => formatProcCostRange(low, high, "en-IN", "₹");
